import { useDeviceStore } from "./store/useDeviceStore";

type NativeWindow = Window & {
  webkit?: {
    messageHandlers?: Record<
      string,
      { postMessage: (data: string) => void } | undefined
    >;
  };
  VoicePark?: Record<string, ((data: string) => void) | undefined>;
};

// 네이티브 앱(WebView) 안에서 실행 중인지 판단한다.
// UA 기반 deviceType은 모바일 웹도 ios/android로 잡히므로, 브릿지 주입 여부로 확인한다.
export function hasNativeBridge() {
  if (typeof window === "undefined") return false;
  const nativeWindow = window as NativeWindow;
  return !!nativeWindow.VoicePark || !!nativeWindow.webkit?.messageHandlers;
}

// 네이티브에 전화 연결을 요청한다. (앱이 없으면 웹 tel: 링크로 동작)
//  - iOS: openSystemURLScheme { url: "tel://01012345678" }
//  - Android: handleIntentEvent { intent: "android.intent.action.DIAL", data: "tel:01012345678" }
// 전화번호는 하이픈 없는 숫자만 전달하기로 네이티브와 합의되어 있다.
export function requestPhoneCall(phoneNumber: string) {
  const digits = phoneNumber.replace(/[^0-9]/g, "");
  if (!digits) return;

  const nativeWindow = window as NativeWindow;
  const openDialer = () => {
    window.location.href = `tel:${digits}`;
  };

  const actions = {
    ios: () => {
      const handler = nativeWindow.webkit?.messageHandlers?.openSystemURLScheme;
      if (!handler) return openDialer();
      handler.postMessage(JSON.stringify({ url: `tel://${digits}` }));
    },
    android: () => {
      const voicePark = nativeWindow.VoicePark;
      if (!voicePark?.handleIntentEvent) return openDialer();
      // 안드로이드 JS 인터페이스는 메서드만 떼어내 호출하면 수신자를 잃어
      // "Java bridge method can't be invoked on a non-injected object" 에러가 난다.
      voicePark.handleIntentEvent(
        JSON.stringify({
          intent: "android.intent.action.DIAL",
          data: `tel:${digits}`,
        }),
      );
    },
    web: openDialer,
  };

  const deviceType = useDeviceStore.getState().deviceType;
  (actions[deviceType] ?? actions.web)();
}

export function postToApp(type: string, payload?: unknown) {
  const msg = payload !== undefined ? JSON.stringify(payload) : undefined;
  // iOS WKWebView
  (window as any).webkit?.messageHandlers?.[type]?.postMessage?.(msg);
  // Android addJavascriptInterface
  if (msg !== undefined) (window as any).VoicePark?.[type]?.(msg);
  else (window as any).VoicePark?.[type]?.();
}

/**
 * 인증번호 발급/재발송 시 AOS SMS receiver 등록을 요청한다.
 * 재요청마다 호출해야 새 receiver가 등록된다. (유효 시간 5분, AOS 측)
 */
export function notifyNativeOtpRequested() {
  if (useDeviceStore.getState().deviceType !== "android") return;
  if (!hasNativeBridge()) return;
  postToApp("onRequestOtp");
}
